import { DEFAULT_DEAD_DEVICE_MONTHS } from '../constants/deadDeviceThreshold';
import { getDefaultRiskTypesCsv } from '../constants/riskTypes';

export const PAGE_SIZE_OPTIONS = [20, 50, 100, 200, 500];
/** Cap per-risk fetch when merging multiple risks client-side (was 500). */
export const MULTI_RISK_FETCH_CAP = 100;

/**
 * Quiet period after the last filter change before a dashboard/status fetch.
 * Long enough to tick several checkboxes / chips without stampeding the backend.
 */
export const FILTER_FETCH_DEBOUNCE_MS = 1500;

/** Serial search draft → filters.q (shorter than filter-panel debounce). */
export const SERIAL_SEARCH_DEBOUNCE_MS = 900;

export const DEFAULT_FILTERS = {
  riskTypes: getDefaultRiskTypesCsv(),
  organizationId: '',
  sort: 'risk_score_desc',
  schedule: '',
  integration: '',
  integrationKeys: '',
  minOverdue: '',
  maxOverdue: '',
  overduePreset: 'all',
  deadDeviceMonths: String(DEFAULT_DEAD_DEVICE_MONTHS),
  riskLevel: '',
  firmwareVersion: '',
  controllerModel: '',
  connectionType: '',
  q: '',
  viewMode: 'table',
  showAll: false,
  hideTest: true,
  hasComment: '',
};

export const OVERDUE_PRESETS = [
  { id: 'all', label: 'Все сроки', min: '', max: '' },
  { id: '1-7', label: '1–7 дн.', min: '1', max: '7' },
  { id: '8-14', label: '8–14 дн.', min: '8', max: '14' },
  { id: '15-30', label: '15–30 дн.', min: '15', max: '30' },
  { id: '31-90', label: '31–90 дн.', min: '31', max: '90' },
  { id: '91+', label: '91+ дн.', min: '91', max: '' },
];

function parsePositiveInt(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function parsePageSize(value) {
  const parsed = Number.parseInt(value, 10);
  return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : PAGE_SIZE_OPTIONS[0];
}

function detectOverduePreset(minOverdue, maxOverdue) {
  const preset = OVERDUE_PRESETS.find((item) => item.min === minOverdue && item.max === maxOverdue);
  return preset?.id || 'custom';
}

function parseCsvList(value) {
  if (!value) {
    return [];
  }

  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseRiskTypes(searchParams) {
  const riskTypesParam = searchParams.get('riskTypes');

  if (riskTypesParam) {
    return parseCsvList(riskTypesParam);
  }

  const legacyRiskType = searchParams.get('riskType');

  if (legacyRiskType) {
    return [legacyRiskType];
  }

  return parseCsvList(DEFAULT_FILTERS.riskTypes);
}

/** Phone (<640): cards by default; desktop/tablet: table. URL wins when set. */
export function getDefaultViewMode() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'table';
  }

  try {
    return window.matchMedia('(max-width: 639px)').matches ? 'cards' : 'table';
  } catch {
    return 'table';
  }
}

function parseViewMode(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'cards' || normalized === 'table') {
    return normalized;
  }

  return getDefaultViewMode();
}

export function parseDashboardQuery(searchParams) {
  const page = parsePositiveInt(searchParams.get('page'), 1);
  const pageSize = parsePageSize(searchParams.get('pageSize'));
  const minOverdue = searchParams.get('minOverdue') ?? '';
  const maxOverdue = searchParams.get('maxOverdue') ?? '';
  const overduePreset = searchParams.get('overduePreset') || detectOverduePreset(minOverdue, maxOverdue);
  const riskTypeIds = parseRiskTypes(searchParams);

  return {
    page,
    pageSize,
    filters: {
      riskTypes: riskTypeIds.join(','),
      organizationId: searchParams.get('organizationId') || '',
      sort: searchParams.get('sort') || DEFAULT_FILTERS.sort,
      schedule: searchParams.get('schedule') || '',
      integration: searchParams.get('integration') || '',
      integrationKeys: searchParams.get('integrationKeys') || '',
      minOverdue,
      maxOverdue,
      overduePreset,
      deadDeviceMonths: searchParams.get('deadDeviceMonths') || DEFAULT_FILTERS.deadDeviceMonths,
      riskLevel: searchParams.get('riskLevel') || '',
      firmwareVersion: searchParams.get('firmwareVersion') || '',
      controllerModel: searchParams.get('controllerModel') || '',
      connectionType: searchParams.get('connectionType') || '',
      q: searchParams.get('q') || '',
      viewMode: parseViewMode(searchParams.get('viewMode')),
      showAll: ['1', 'true', 'yes'].includes(String(searchParams.get('showAll') || '').toLowerCase()),
      hideTest: !['0', 'false', 'no', 'off'].includes(
        String(searchParams.get('hideTest') ?? '1').toLowerCase(),
      ),
      hasComment: searchParams.get('hasComment') || '',
    },
  };
}

export function buildRiskQuery({
  page,
  pageSize,
  sort,
  schedule,
  integration,
  integrationKeys,
  minOverdue,
  maxOverdue,
  deadDeviceMonths,
  riskLevel,
  firmwareVersion,
  controllerModel,
  connectionType,
  q,
  showAll,
  hideTest,
  hasComment,
  organizationId,
  integrationsAvailable,
}) {
  const params = new URLSearchParams({
    limit: String(pageSize),
    offset: String((page - 1) * pageSize),
    sort,
    dead_device_months: String(deadDeviceMonths || DEFAULT_DEAD_DEVICE_MONTHS),
  });

  if (organizationId) {
    params.set('organization_id', String(organizationId));
  }

  if (schedule) {
    params.set('schedule', schedule);
  }

  if (integrationKeys) {
    params.set('integration_key', integrationKeys);
  } else if (integration && integrationsAvailable) {
    params.set('has_integration', integration);
  }

  if (minOverdue !== '') {
    params.set('min_overdue', String(minOverdue));
  }

  if (maxOverdue !== '') {
    params.set('max_overdue', String(maxOverdue));
  }

  if (riskLevel) {
    params.set('risk_level', riskLevel);
  }

  if (firmwareVersion) {
    params.set('firmware_version', firmwareVersion);
  }

  if (controllerModel) {
    params.set('controller_model', controllerModel);
  }

  if (connectionType) {
    params.set('connection_type', connectionType);
  }

  if (q) {
    params.set('q', q);
  }

  if (showAll) {
    params.set('show_all', 'true');
  }

  if (hideTest === false) {
    params.set('hide_test', '0');
  }

  if (hasComment === '1' || hasComment === '0') {
    params.set('has_comment', hasComment);
  }

  return params.toString();
}

export function buildDashboardSearchParams({ page, pageSize, filters }) {
  const params = new URLSearchParams();

  if (page > 1) {
    params.set('page', String(page));
  }

  if (pageSize !== PAGE_SIZE_OPTIONS[0]) {
    params.set('pageSize', String(pageSize));
  }

  if (filters.riskTypes) {
    params.set('riskTypes', filters.riskTypes);
  }

  if (filters.sort && filters.sort !== DEFAULT_FILTERS.sort) {
    params.set('sort', filters.sort);
  }

  if (filters.schedule) {
    params.set('schedule', filters.schedule);
  }

  if (filters.integration) {
    params.set('integration', filters.integration);
  }

  if (filters.integrationKeys) {
    params.set('integrationKeys', filters.integrationKeys);
  }

  if (filters.minOverdue !== '') {
    params.set('minOverdue', String(filters.minOverdue));
  }

  if (filters.maxOverdue !== '') {
    params.set('maxOverdue', String(filters.maxOverdue));
  }

  if (filters.overduePreset && filters.overduePreset !== 'all') {
    params.set('overduePreset', filters.overduePreset);
  }

  if (filters.deadDeviceMonths && filters.deadDeviceMonths !== DEFAULT_FILTERS.deadDeviceMonths) {
    params.set('deadDeviceMonths', String(filters.deadDeviceMonths));
  }

  if (filters.riskLevel) {
    params.set('riskLevel', filters.riskLevel);
  }

  if (filters.firmwareVersion) {
    params.set('firmwareVersion', filters.firmwareVersion);
  }

  if (filters.controllerModel) {
    params.set('controllerModel', filters.controllerModel);
  }

  if (filters.connectionType) {
    params.set('connectionType', filters.connectionType);
  }

  if (filters.q) {
    params.set('q', filters.q);
  }

  if (filters.viewMode && filters.viewMode !== DEFAULT_FILTERS.viewMode) {
    params.set('viewMode', filters.viewMode);
  }

  if (filters.showAll) {
    params.set('showAll', '1');
  }

  if (filters.hideTest === false) {
    params.set('hideTest', '0');
  }

  if (filters.hasComment === '1' || filters.hasComment === '0') {
    params.set('hasComment', filters.hasComment);
  }

  if (filters.organizationId) {
    params.set('organizationId', String(filters.organizationId));
  }

  return params;
}

export function buildDashboardQueryString(state) {
  const params = buildDashboardSearchParams(state);
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Query string without leading `?` — for saving filter presets. */
export function serializeDashboardQuery(state) {
  return buildDashboardSearchParams(state).toString();
}

/** Fetch key excludes display-only fields (viewMode) so table/cards does not refetch. */
export function serializeDashboardFetchKey(state) {
  const filters = { ...(state?.filters || {}) };
  delete filters.viewMode;
  return serializeDashboardQuery({
    page: state?.page,
    pageSize: state?.pageSize,
    filters,
  });
}
